import { getDevsiteFullPath } from 'shared/utils/devsite';
import { getToken } from 'modules/core/auth/token';
import {
  Executor,
  HeaderKeys,
  HttpClientResponse,
  MimeType,
  QueryParameters,
  RequestHeaders,
  RequestMethod,
} from './Definitions';
import { Interceptor } from './interceptors';
import { getQueryParameters } from './utils';

export interface AuthorizationOptions {
  type: 'Bearer' | 'ID';
  header?: string; // defaults to Authorization
  customAuthFetcher?: () => string;
}

export interface RequestExecutorOptions {
  authorization?: AuthorizationOptions;
  defaultHeaders?: RequestHeaders;
  csrfToken?: () => string | Promise<string>;
}

export class RequestExecutor<T extends string> implements Executor<T> {
  public responseInterceptors: Interceptor[] = [];

  private requiredParams: Partial<RequestInit> = {
    credentials: 'same-origin',
  };

  private readonly urlPrefix: string;
  private readonly options: RequestExecutorOptions;

  constructor(urlPrefix: string = '', options: RequestExecutorOptions = { defaultHeaders: {} }) {
    this.urlPrefix = urlPrefix.startsWith('/') ? getDevsiteFullPath(urlPrefix) : urlPrefix;
    this.options = options;
  }

  withInterceptor(responseInterceptor: Interceptor) {
    this.responseInterceptors.push(responseInterceptor);
    return this;
  }

  fullUrl(path: string): string {
    return `${this.urlPrefix}${path}`;
  }

  async fullWebSocketUrl(path: string): Promise<string> {
    return `${this.urlPrefix.replace('http', 'ws')}${path}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  executeMethod(method: RequestMethod, path: T, body: any, headers?: RequestHeaders) {
    switch (method) {
      case RequestMethod.GET:
        return this.get(path, headers);
      case RequestMethod.PATCH:
        return this.patch(path, body, headers);
      case RequestMethod.PUT:
        return this.put(path, body, headers);
      case RequestMethod.POST:
        return this.post(path, body, headers);
      case RequestMethod.DELETE:
        return this.delete(path, headers);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  get<U = any>(
    url: T,
    headers?: RequestHeaders,
    queryParams?: QueryParameters,
    additionalParams?: Partial<RequestInit>
  ): Promise<HttpClientResponse<U>> {
    const requestInit = additionalParams || ([] as Partial<RequestInit>);
    return this.execute(url, { method: RequestMethod.GET, ...requestInit }, headers, queryParams);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  post<U = any>(
    url: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
    body?: any,
    headers?: RequestHeaders,
    queryParams?: QueryParameters,
    additionalParams?: Partial<RequestInit>
  ): Promise<HttpClientResponse<U>> {
    const requestInit = additionalParams || ([] as Partial<RequestInit>);
    return this.execute(url, { method: RequestMethod.POST, body, ...requestInit }, headers, queryParams);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  put<U = any>(
    url: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
    body?: any,
    headers?: RequestHeaders,
    queryParams?: QueryParameters
  ): Promise<HttpClientResponse<U>> {
    return this.execute(url, { method: RequestMethod.PUT, body }, headers, queryParams);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  delete<U = any>(url: T, headers?: RequestHeaders, queryParams?: QueryParameters): Promise<HttpClientResponse<U>> {
    return this.execute(url, { method: RequestMethod.DELETE }, headers, queryParams);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  patch<U = any>(
    url: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
    body: any,
    headers?: RequestHeaders,
    queryParams?: QueryParameters
  ): Promise<HttpClientResponse<U>> {
    return this.execute(url, { method: RequestMethod.PATCH, body }, headers, queryParams);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  protected async execute<U = any>(
    url: string,
    params: Partial<RequestInit>,
    headers?: RequestHeaders,
    queryParams?: QueryParameters,
    init?: RequestInit
  ): Promise<HttpClientResponse<U>> {
    const combinedHeaders = { ...(this.options?.defaultHeaders || {}), ...headers };

    if (combinedHeaders['Content-Type'] === MimeType.MULTIPART) {
      // Remove the 'Content-Type' header so that the browser can include the boundary.
      // https://muffinman.io/uploading-files-using-fetch-multipart-form-data/
      delete combinedHeaders['Content-Type'];
    }

    if (this.options.csrfToken) {
      combinedHeaders['xsrf-token'] = await this.options.csrfToken();
    }

    if (this.options.authorization) {
      const token = this.getAuthorization();
      if (token) {
        const header = this.options.authorization.header || HeaderKeys.AUTHORIZATION;
        combinedHeaders[header as HeaderKeys] = `${this.options.authorization.type} ${token}`;
      }
    }

    const allParams: RequestInit = this.prepRequestParams(params, { ...combinedHeaders });

    const fullUrl = this.fullUrl(url);
    const request: Request = new Request(`${fullUrl}${getQueryParameters(queryParams)}`, allParams);
    const requestHeaders: RequestHeaders = this.toRequestHeaders(request.headers);

    const requestPromise = fetch(request, init)
      .then((resp) => this.processResponse(resp, params.method as RequestMethod))
      .then(({ responseHeaders, body, response }) => {
        if (!response.ok) {
          return Promise.reject({ body, response });
        }

        return Promise.resolve({ requestHeaders, responseHeaders, body, response });
      });

    return this.responseInterceptors.reduce((accum, currentInterceptor) => {
      let chain = accum;
      if (currentInterceptor.success) {
        chain = chain.then(currentInterceptor.success);
      }
      if (currentInterceptor.error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
        chain = chain.catch(currentInterceptor.error) as any;
      }
      return chain;
    }, requestPromise);
  }

  private getAuthorization() {
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    if (this.options.authorization.customAuthFetcher) return this.options.authorization.customAuthFetcher();
    return getToken();
  }

  private processResponse = (
    response: Response,
    requestMethod: RequestMethod
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  ): Promise<{ responseHeaders: RequestHeaders; body: any; response: Response }> => {
    const responseHeaders = this.toRequestHeaders(response.headers);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
    return this.getResponseBody(response, requestMethod).then((body: any) => ({ responseHeaders, body, response }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  private async getResponseBody(response: Response, requestMethod: RequestMethod): Promise<any> {
    const contentType = response.headers.get(HeaderKeys.CONTENT_TYPE);
    // Normalize to the first media type without parameters. Some servers/mocks emit
    // comma-joined values (e.g. "application/json, application/json") or include a
    // charset (e.g. "application/json; charset=utf-8"); match on the bare media type.
    const mediaType = contentType?.split(',')[0].split(';')[0].trim().toLowerCase();

    if (response.status === 204 || response.status === 205) {
      return Promise.resolve('');
    }

    if (mediaType?.startsWith('image')) {
      return response.blob();
    }

    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    if (parseInt(response.headers.get(HeaderKeys.CONTENT_LENGTH)) === 0) {
      return Promise.resolve('');
    }

    if (mediaType?.startsWith(MimeType.EVENT_STREAM)) {
      return response.body;
    }

    switch (mediaType) {
      case MimeType.ZIP:
      case MimeType.PDF:
      case MimeType.AUDIO:
      case MimeType.MPEG:
      case MimeType.OGG:
        return response.blob();
      case MimeType.JSON:
        return response.json().catch((error) => {
          if (requestMethod === RequestMethod.DELETE) {
            return {};
          }
          throw error;
        });
      default:
        return response.text();
    }
  }

  // The Headers object information does not get copied outside of the fetch() API,
  // probably for security reasons. In the case of the API Explorer we do want to see
  // the header information and must copy it to reference at a later time.
  private toRequestHeaders(headers: Headers): RequestHeaders {
    const requestHeaders: RequestHeaders = {};
    headers.forEach((value: string, key: HeaderKeys) => {
      requestHeaders[key] = value;
    });
    return requestHeaders;
  }

  private prepHeaders(headers: RequestHeaders): Headers {
    const head: Headers = new Headers();
    for (const key of Object.keys(headers)) {
      // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
      head.append(key, headers[key as HeaderKeys]);
    }

    return head;
  }

  private prepRequestParams(additionalParams: Partial<RequestInit>, headers: RequestHeaders): RequestInit {
    const headerz: Headers = this.prepHeaders(headers);
    return { ...this.requiredParams, ...additionalParams, headers: headerz };
  }
}
